import { getCanonicalDomain } from "@guardian/shared-utils";

/**
 * Extract the leading subdomain from a `<slug>.<canonical>` hostname.
 *
 * Returns the slug ONLY when the hostname matches `<slug>.<canonical>` where
 * `<slug>` matches `^[a-z0-9-]{3,40}$` (the database slug CHECK constraint)
 * AND is NOT one of the reserved bare hostnames (the canonical itself,
 * `www.<canonical>`, `form.guardiancheckin.com`, `localhost`, IP literals).
 *
 * Otherwise returns null. The hostname is case-folded to lowercase before
 * matching.
 *
 * # WHY: getCanonicalDomain() is called on every invocation (NOT captured at
 * module load). The urls.ts:1-6 contract notes vi.stubEnv() in tests cannot
 * mutate a top-level captured const. Honoring that contract here is the whole
 * point of the env-driven canonical domain — on dev builds the canonical is
 * `dev.gcheckin.com`, so `dev.gcheckin.com` and `www.dev.gcheckin.com` must
 * resolve to null and `<slug>.dev.gcheckin.com` must yield the slug.
 */
export function extractBrandSlug(hostname: string): string | null {
  if (!hostname) return null;
  // WHY .replace(/\.$/, ""): FQDN trailing-dot is legal hostname syntax that some
  // DNS-strict resolvers expose. Without stripping it the regex below misses
  // `<slug>.<canonical>.` and we'd misclassify a branded subdomain as canonical.
  const host = (hostname.split(":")[0] ?? "").toLowerCase().replace(/\.$/, "");
  const canonical = getCanonicalDomain().toLowerCase();
  if (host === canonical || host === `www.${canonical}`) return null;
  if (host === "form.guardiancheckin.com") return null;
  if (host === "localhost" || /^\d+\.\d+\.\d+\.\d+$/.test(host)) return null;
  const canonicalEscaped = canonical.replace(/\./g, "\\.");
  const pattern = new RegExp(`^([a-z0-9-]{3,40})\\.${canonicalEscaped}$`);
  const match = host.match(pattern);
  return match && match[1] ? match[1] : null;
}

/**
 * Convert a hex color (#RRGGBB or #RRGGBBAA — alpha ignored) into the
 * "H S% L%" integer-component string used by Tailwind's `hsl(var(--primary))`
 * form. Output matches the existing :root tokens (e.g., "230 85% 56%").
 *
 * Throws on invalid hex.
 */
export function hexToHslComponents(hex: string): string {
  const clean = hex.replace(/^#/, "").slice(0, 6);
  if (!/^[0-9a-fA-F]{6}$/.test(clean)) {
    throw new Error(`hexToHslComponents: invalid hex "${hex}"`);
  }
  const r = parseInt(clean.slice(0, 2), 16) / 255;
  const g = parseInt(clean.slice(2, 4), 16) / 255;
  const b = parseInt(clean.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h *= 60;
  }
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}
